/**
 * Restore — Snapshot purge confirmation modal — per docs/specs/restore.md §6.
 *
 * Owner-only. Releases R2 storage held by a rolling snapshot.
 *
 * @copyright 2025 Glimbr Pty Ltd
 * @version 2026-04-21-v2
 */
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Modal } from '../components/modal';
import { RoleButton } from '../components/role-gate';
import { useToast } from '../components/toast';
import { HttpError, apiPost, getHttpErrorMessage } from '../../lib/http';
import { useAdminSession } from '../../lib/auth';
import { appendAuditEntry } from '../../lib/mutations';
import type { SnapshotRow } from '../../lib/types';

export function RestoreSnapshotPurgeModal({
    snapshot,
    onClose
}: {
    snapshot: SnapshotRow | null;
    onClose: () => void;
}) {
    const qc = useQueryClient();
    const { toast } = useToast();
    const session = useAdminSession();
    const [reason, setReason] = useState('');
    const [confirmPhrase, setConfirmPhrase] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const canSubmit = reason.trim().length > 0 && confirmPhrase.trim().toUpperCase() === 'DELETE';

    const isNotIntegrated = (error: unknown) =>
        error instanceof HttpError && [404, 405, 501].includes(error.status);

    const reset = () => {
        setReason('');
        setConfirmPhrase('');
    };

    const handleClose = () => {
        if (submitting) return;
        reset();
        onClose();
    };

    if (!snapshot) return null;

    const handleConfirm = async () => {
        if (!snapshot || !canSubmit || submitting) return;
        setSubmitting(true);

        try {
            await apiPost(`/api/admin/restore/snapshots/${snapshot.id}/purge`, {
                reason: reason.trim()
            });
            await Promise.all([
                qc.invalidateQueries({ queryKey: ['admin', 'restore', 'snapshots'] }),
                qc.invalidateQueries({ queryKey: ['admin', 'restore', 'kpis'] })
            ]);
            appendAuditEntry(qc, {
                actor: session.email,
                actor_role: session.role,
                action: 'snapshot.purge',
                target_type: 'snapshot',
                target_id: snapshot.id,
                severity: 'destructive',
                reason: reason.trim()
            });
            toast(`Snapshot purge requested · ${snapshot.size_gb.toFixed(1)} GB`, {
                kind: 'danger',
                detail: snapshot.id
            });
            handleClose();
        } catch (error) {
            if (isNotIntegrated(error)) {
                appendAuditEntry(qc, {
                    actor: session.email,
                    actor_role: session.role,
                    action: 'snapshot.purge',
                    target_type: 'snapshot',
                    target_id: snapshot.id,
                    severity: 'destructive',
                    reason: `[DRY-RUN] ${reason.trim()}`
                });
                toast('Dry-run recorded', {
                    kind: 'info',
                    detail: `Snapshot purge not yet wired to worker for ${snapshot.id}`
                });
                handleClose();
            } else {
                toast('Purge failed', {
                    kind: 'danger',
                    detail: getHttpErrorMessage(error, 'Could not purge snapshot now')
                });
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal
            open={!!snapshot}
            onClose={handleClose}
            title="Purge snapshot"
            subtitle={snapshot.id}
            width={440}
            footer={
                <>
                    <button className="btn btn-ghost" onClick={handleClose} disabled={submitting}>
                        Cancel
                    </button>
                    <RoleButton
                        scope="owner_only"
                        className="btn btn-primary"
                        onClick={handleConfirm}
                        disabled={!canSubmit || submitting}
                    >
                        {submitting ? 'Purging…' : `Release ${snapshot.size_gb.toFixed(1)} GB`}
                    </RoleButton>
                </>
            }
        >
            <p
                style={{
                    fontSize: 13,
                    color: 'var(--text-primary)',
                    margin: '0 0 8px'
                }}
            >
                Release <strong>{snapshot.size_gb.toFixed(1)} GB</strong> of R2 storage — this
                cannot be undone.
            </p>
            <p
                style={{
                    fontSize: 12,
                    color: 'var(--text-muted)',
                    margin: 0
                }}
            >
                Created {snapshot.created_at} · covers {snapshot.users_covered.toLocaleString()}{' '}
                users · {snapshot.retention_days} days retention remaining.
            </p>
            <div className="form-field">
                <label className="form-label" htmlFor="snapshot-purge-reason">
                    Reason (required)
                </label>
                <textarea
                    id="snapshot-purge-reason"
                    className="form-textarea"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="e.g. snapshot retained only for legal hold extension #LD-221"
                    style={{ minHeight: 70 }}
                />
                <div className="form-hint">This reason is logged on the admin audit trail.</div>
            </div>
            <div className="form-field">
                <label className="form-label" htmlFor="snapshot-purge-confirm">
                    Type DELETE to confirm
                </label>
                <input
                    id="snapshot-purge-confirm"
                    className="form-input mono"
                    value={confirmPhrase}
                    onChange={(e) => setConfirmPhrase(e.target.value)}
                    placeholder="DELETE"
                />
                <div className="form-hint">
                    Extra confirmation avoids accidental snapshot release.
                </div>
            </div>
        </Modal>
    );
}
